import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccounts } from '../../hooks/useAccounts';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { useAuth } from '../../contexts/AuthContext';
import { syncAllAccountBalances } from '../../lib/accountBalance';
import { PlanLimitAlert } from '../../components/feature/PlanLimitAlert';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { AccountModal } from './components/AccountModal';
import { AccountDetailModal } from './components/AccountDetailModal';
import { TransferModal } from './components/TransferModal';
import { useTransfers } from '../../hooks/useTransfers';
import type { Account } from '../../hooks/useAccounts';

const TYPE_LABELS: Record<string, string> = {
  corrente: 'Conta Corrente', digital: 'Conta Digital', poupanca: 'Poupança',
  carteira: 'Carteira', investimento: 'Investimentos', dinheiro: 'Dinheiro em Espécie',
  corretora: 'Corretora', dolar: 'Conta em Dólar', euro: 'Conta em Euro',
  outra_moeda: 'Outra Moeda', cripto: 'Criptoativos', internacional: 'Internacional', pj: 'Conta PJ',
};
const TYPE_ICONS: Record<string, string> = {
  corrente: 'ri-bank-line', digital: 'ri-smartphone-line', poupanca: 'ri-safe-line',
  carteira: 'ri-wallet-3-line', investimento: 'ri-stock-line', dinheiro: 'ri-money-dollar-circle-line',
  corretora: 'ri-line-chart-line', dolar: 'ri-currency-line', euro: 'ri-currency-line',
  outra_moeda: 'ri-exchange-line', cripto: 'ri-bit-coin-line', internacional: 'ri-global-line', pj: 'ri-building-line',
};

type ToastType = 'success' | 'error' | 'warning';
interface Toast { id: number; message: string; type: ToastType; }
const TOAST_COLORS: Record<ToastType, string> = { success: 'bg-emerald-500', error: 'bg-red-500', warning: 'bg-amber-500' };
const TOAST_ICONS: Record<ToastType, string> = { success: 'ri-check-line', error: 'ri-error-warning-line', warning: 'ri-alert-line' };
const formatCurrency = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

type SampleTransaction = { id: string; description: string; type: string; amount: number; date: string; status: string; category: string };

type ConfirmAction =
  | { type: 'archive'; account: Account }
  | { type: 'delete'; account: Account }
  | { type: 'deleteBlocked'; account: Account; transactionCount: number; sampleTransactions: SampleTransaction[] }
  | { type: 'unarchive'; account: Account };

export default function ContasPage() {
  const [showArchived, setShowArchived] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { accounts, loading, totalBalance, createAccount, updateAccount, archiveAccount, unarchiveAccount, deleteAccount, getAccountStats, refetch } = useAccounts(showArchived);
  const { checkAccountLimit, isPaid } = usePlanLimits();
  const { createTransfer } = useTransfers();

  const [showModal, setShowModal] = useState(false);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [detailAccount, setDetailAccount] = useState<Account | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const limitCheck = checkAccountLimit();

  const addToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  const handleRecalculate = async () => {
    if (!user || isRecalculating) return;
    setIsRecalculating(true);
    try {
      await syncAllAccountBalances(user.id);
      await refetch();
      addToast('Saldos recalculados com sucesso!', 'success');
    } catch { addToast('Erro ao recalcular saldos.', 'error'); }
    finally { setIsRecalculating(false); }
  };

  const handleNewAccount = () => {
    if (!limitCheck.allowed) { setShowUpgrade(true); return; }
    setEditAccount(null); setShowModal(true);
  };

  const handleEdit = (account: Account) => { setEditAccount(account); setShowModal(true); };

  const handleSave = async (values: Omit<Account, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'is_archived'>) => {
    if (editAccount) {
      const result = await updateAccount(editAccount.id, values);
      if (result.error) { addToast('Erro ao atualizar conta.', 'error'); return result; }
      addToast('Conta atualizada com sucesso!'); return result;
    } else {
      const result = await createAccount(values);
      if (result.error) { addToast('Erro ao criar conta.', 'error'); return result; }
      addToast('Conta criada com sucesso!'); return result;
    }
  };

  const handleArchiveConfirm = async () => {
    if (!confirmAction || confirmAction.type !== 'archive') return;
    setActionLoading(true);
    const result = await archiveAccount(confirmAction.account.id);
    setActionLoading(false); setConfirmAction(null);
    if (result.error) addToast('Erro ao arquivar conta.', 'error');
    else addToast('Conta arquivada. O histórico foi mantido.', 'warning');
  };

  const handleUnarchiveConfirm = async () => {
    if (!confirmAction || confirmAction.type !== 'unarchive') return;
    setActionLoading(true);
    const result = await unarchiveAccount(confirmAction.account.id);
    setActionLoading(false); setConfirmAction(null);
    if (result.error) addToast('Erro ao reativar conta.', 'error');
    else addToast('Conta reativada com sucesso!');
  };

  const handleDeleteRequest = async (account: Account) => {
    setActionLoading(true);
    const result = await deleteAccount(account.id);
    setActionLoading(false);
    if (result.hasTransactions) {
      setConfirmAction({
        type: 'deleteBlocked',
        account,
        transactionCount: result.transactionCount ?? 0,
        sampleTransactions: result.sampleTransactions ?? [],
      });
    } else if (result.error) { addToast('Erro ao excluir conta.', 'error'); }
    else { setConfirmAction(null); addToast('Conta excluída com sucesso.'); }
  };

  const handleTransfer = async (payload: { from_account_id: string; to_account_id: string; amount: number; date: string; notes?: string; status: 'pago' | 'pendente' }) => {
    const result = await createTransfer(payload);
    if (result.success) {
      await refetch();
      addToast(result.message, 'success');
    }
    return result;
  };

  const activeAccounts = accounts.filter(a => !a.is_archived);
  const archivedAccounts = accounts.filter(a => a.is_archived);
  const getStatsCallback = useCallback((id: string) => getAccountStats(id), [getAccountStats]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Minhas Contas</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gerencie suas contas e acompanhe os saldos</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleRecalculate}
            disabled={isRecalculating || loading}
            title="Recalcular todos os saldos"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-600 border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className={`ri-refresh-line ${isRecalculating ? 'animate-spin' : ''}`} />
            {isRecalculating ? 'Recalculando...' : 'Recalcular saldos'}
          </button>
          <button
            onClick={() => setShowArchived(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-600 border transition-colors cursor-pointer whitespace-nowrap ${showArchived ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
          >
            <i className="ri-archive-line" />
            {showArchived ? 'Ocultar arquivadas' : 'Ver arquivadas'}
            {!showArchived && archivedAccounts.length > 0 && (
              <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">{archivedAccounts.length}</span>
            )}
          </button>
          {activeAccounts.length >= 2 && (
            <button
              onClick={() => setShowTransferModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-600 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-right-line" />
              Transferir
            </button>
          )}
          <button
            onClick={handleNewAccount}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Nova Conta
            {!isPaid && limitCheck.limit !== null && (
              <span className="ml-1 text-emerald-200 text-xs">({limitCheck.current}/{limitCheck.limit})</span>
            )}
          </button>
        </div>
      </div>

      {!isPaid && limitCheck.limit !== null && (
        <PlanLimitAlert current={limitCheck.current} limit={limitCheck.limit} resource="contas" onUpgrade={() => setShowUpgrade(true)} />
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-40 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : activeAccounts.length === 0 && !showArchived ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
            <i className="ri-bank-line text-3xl text-emerald-400" />
          </div>
          <p className="text-base font-700 text-gray-700 mb-1">Nenhuma conta cadastrada</p>
          <p className="text-sm text-gray-400 mb-6 max-w-xs">Adicione suas contas bancárias, carteiras e investimentos para começar.</p>
          <button onClick={handleNewAccount} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> Cadastrar primeira conta
          </button>
        </div>
      ) : (
        <>
          {activeAccounts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeAccounts.map(account => (
                <AccountCard key={account.id} account={account}
                  onView={() => setDetailAccount(account)} onEdit={() => handleEdit(account)}
                  onArchive={() => setConfirmAction({ type: 'archive', account })}
                  onDelete={() => handleDeleteRequest(account)}
                  onExtrato={() => navigate(`/contas/extrato/${account.id}`)} />
              ))}
            </div>
          )}
          {showArchived && archivedAccounts.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <p className="text-sm font-700 text-amber-600">Contas arquivadas</p>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{archivedAccounts.length}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedAccounts.map(account => (
                  <AccountCard key={account.id} account={account} archived
                    onView={() => setDetailAccount(account)} onEdit={() => handleEdit(account)}
                    onArchive={() => setConfirmAction({ type: 'unarchive', account })}
                    onDelete={() => handleDeleteRequest(account)}
                    onExtrato={() => navigate(`/contas/extrato/${account.id}`)} />
                ))}
              </div>
            </div>
          )}
          {activeAccounts.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <i className="ri-pie-chart-line text-emerald-500 text-lg" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Saldo total consolidado</p>
                    <p className={`text-2xl font-700 ${totalBalance >= 0 ? 'text-gray-900' : 'text-red-500'}`}>
                      {totalBalance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <p className="text-xs text-gray-400">{activeAccounts.length} conta{activeAccounts.length !== 1 ? 's' : ''} ativa{activeAccounts.length !== 1 ? 's' : ''}</p>
                  <button onClick={handleRecalculate} disabled={isRecalculating}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-600 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 border border-gray-100 hover:border-emerald-200 disabled:opacity-50 cursor-pointer transition-all whitespace-nowrap">
                    <i className={`ri-refresh-line text-xs ${isRecalculating ? 'animate-spin' : ''}`} />
                    {isRecalculating ? 'Recalculando...' : 'Recalcular'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Toasts */}
      <div className="fixed top-5 right-5 z-[60] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2.5 px-4 py-3 ${TOAST_COLORS[t.type]} text-white rounded-xl text-sm font-500 pointer-events-auto`}>
            <i className={`${TOAST_ICONS[t.type]} text-base`} />{t.message}
          </div>
        ))}
      </div>

      {/* Modals */}
      <AccountModal open={showModal} account={editAccount}
        onClose={() => { setShowModal(false); setEditAccount(null); }} onSave={handleSave} />

      <AccountDetailModal open={!!detailAccount} account={detailAccount}
        onClose={() => setDetailAccount(null)} onEdit={handleEdit}
        onArchive={a => setConfirmAction({ type: 'archive', account: a })}
        onDelete={a => handleDeleteRequest(a)}
        getStats={getStatsCallback}
        onRecalculate={async (accountId) => {
          const { syncAccountBalance } = await import('../../lib/accountBalance');
          await syncAccountBalance(accountId); await refetch();
          addToast('Saldo recalculado!', 'success');
        }} />

      <TransferModal
        open={showTransferModal}
        accounts={accounts}
        onClose={() => setShowTransferModal(false)}
        onSave={handleTransfer}
      />

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)}
        title="Limite de contas atingido"
        description={`O plano Free permite apenas ${limitCheck.limit} conta. Faça upgrade para adicionar contas ilimitadas.`} />

      {confirmAction?.type === 'archive' && (
        <ConfirmDialog icon="ri-archive-line" iconBg="bg-amber-50" iconColor="text-amber-500"
          title="Arquivar conta?" description={`A conta "${confirmAction.account.name}" será ocultada. O histórico é mantido.`}
          confirmLabel="Arquivar" confirmClass="bg-amber-500 hover:bg-amber-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)} onConfirm={handleArchiveConfirm} />
      )}
      {confirmAction?.type === 'unarchive' && (
        <ConfirmDialog icon="ri-restart-line" iconBg="bg-emerald-50" iconColor="text-emerald-500"
          title="Reativar conta?" description={`A conta "${confirmAction.account.name}" voltará à listagem principal.`}
          confirmLabel="Reativar" confirmClass="bg-emerald-500 hover:bg-emerald-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)} onConfirm={handleUnarchiveConfirm} />
      )}
      {confirmAction?.type === 'deleteBlocked' && (
        <DeleteBlockedDialog
          account={confirmAction.account}
          transactionCount={confirmAction.transactionCount}
          sampleTransactions={confirmAction.sampleTransactions}
          loading={actionLoading}
          onCancel={() => setConfirmAction(null)}
          onArchive={() => setConfirmAction({ type: 'archive', account: confirmAction.account })}
        />
      )}
      {confirmAction?.type === 'delete' && (
        <ConfirmDialog icon="ri-delete-bin-line" iconBg="bg-red-50" iconColor="text-red-500"
          title="Excluir conta?" description={`Esta ação é irreversível. A conta "${confirmAction.account.name}" será excluída permanentemente.`}
          confirmLabel="Excluir" confirmClass="bg-red-500 hover:bg-red-600"
          loading={actionLoading} onCancel={() => setConfirmAction(null)}
          onConfirm={() => handleDeleteRequest(confirmAction.account)} />
      )}
    </div>
  );
}

/* ─── Account Card ─── */
interface AccountCardProps {
  account: Account; archived?: boolean;
  onView: () => void; onEdit: () => void; onArchive: () => void; onDelete: () => void;
  onExtrato: () => void;
}
function AccountCard({ account, archived = false, onView, onEdit, onArchive, onDelete, onExtrato }: AccountCardProps) {
  const icon = TYPE_ICONS[account.type] || 'ri-bank-line';
  const label = TYPE_LABELS[account.type] || 'Conta';
  return (
    <div className={`bg-white rounded-2xl border transition-all group cursor-pointer ${archived ? 'border-amber-100 opacity-70 hover:opacity-100' : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'}`} onClick={onView}>
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: account.color + '20' }}>
              <i className={`${icon} text-lg`} style={{ color: account.color }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-700 text-gray-900">{account.name}</p>
                {archived && <span className="text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-500">Arquivada</span>}
              </div>
              <p className="text-xs text-gray-400">{account.bank || label}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
            <button onClick={onEdit} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 cursor-pointer" title="Editar">
              <i className="ri-edit-line text-sm" />
            </button>
            <button onClick={onArchive} className={`w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer ${archived ? 'hover:bg-emerald-50 text-gray-400 hover:text-emerald-600' : 'hover:bg-amber-50 text-gray-400 hover:text-amber-600'}`} title={archived ? 'Reativar' : 'Arquivar'}>
              <i className={`text-sm ${archived ? 'ri-restart-line' : 'ri-archive-line'}`} />
            </button>
            <button onClick={onDelete} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 cursor-pointer" title="Excluir">
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>
        <div className="mb-3">
          <p className="text-xs text-gray-400 mb-0.5">Saldo atual</p>
          <p className={`text-xl font-700 ${account.current_balance >= 0 ? 'text-gray-900' : 'text-red-500'}`}>
            {formatCurrency(account.current_balance)}
          </p>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg text-xs text-gray-500 font-500">
            <i className={`${icon} text-xs`} />{label}
          </span>
          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            <button
              onClick={onExtrato}
              className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap"
              title="Ver extrato detalhado"
            >
              <i className="ri-file-list-3-line text-xs" />
              Extrato
            </button>
            <span className="text-xs text-gray-400 flex items-center gap-1 cursor-pointer hover:text-gray-600" onClick={onView}><i className="ri-eye-line text-xs" />Detalhes</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Delete Blocked Dialog ─── */
interface DeleteBlockedDialogProps {
  account: Account;
  transactionCount: number;
  sampleTransactions: SampleTransaction[];
  loading: boolean;
  onCancel: () => void;
  onArchive: () => void;
}
function DeleteBlockedDialog({ account, transactionCount, sampleTransactions, loading, onCancel, onArchive }: DeleteBlockedDialogProps) {
  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  const isIncome = (type: string) => type === 'receita' || type === 'income';
  const STATUS_LABELS: Record<string, string> = { pago: 'Pago', recebido: 'Recebido', pendente: 'Pendente', vencido: 'Vencido' };
  const STATUS_COLORS: Record<string, string> = {
    pago: 'bg-emerald-100 text-emerald-700', recebido: 'bg-emerald-100 text-emerald-700',
    pendente: 'bg-amber-100 text-amber-700', vencido: 'bg-red-100 text-red-700',
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-amber-500 text-xl" />
            </div>
            <div>
              <h3 className="text-base font-700 text-gray-900 mb-1">Conta com movimentações</h3>
              <p className="text-sm text-gray-500">
                A conta <span className="font-600 text-gray-700">{account.name}</span> possui{' '}
                <span className="font-700 text-amber-600">{transactionCount} lançamento{transactionCount !== 1 ? 's' : ''}</span>{' '}
                vinculado{transactionCount !== 1 ? 's' : ''} e não pode ser excluída diretamente.
              </p>
            </div>
          </div>
        </div>

        {/* Transactions preview */}
        {sampleTransactions.length > 0 && (
          <div className="mx-6 mb-4 border border-gray-100 rounded-xl overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-xs font-700 text-gray-500 uppercase tracking-wide">
                Últimas movimentações{transactionCount > sampleTransactions.length ? ` (mostrando ${sampleTransactions.length} de ${transactionCount})` : ''}
              </p>
            </div>
            <div className="divide-y divide-gray-50">
              {sampleTransactions.map(tx => (
                <div key={tx.id} className="flex items-center justify-between px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${isIncome(tx.type) ? 'bg-emerald-50' : 'bg-red-50'}`}>
                      <i className={`text-xs ${isIncome(tx.type) ? 'ri-arrow-down-line text-emerald-500' : 'ri-arrow-up-line text-red-500'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-600 text-gray-800 truncate">{tx.description}</p>
                      <p className="text-[11px] text-gray-400">{tx.category} · {fmtDate(tx.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-500 ${STATUS_COLORS[tx.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_LABELS[tx.status] ?? tx.status}
                    </span>
                    <p className={`text-xs font-700 ${isIncome(tx.type) ? 'text-emerald-600' : 'text-red-500'}`}>
                      {isIncome(tx.type) ? '+' : '-'}{fmt(tx.amount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendation */}
        <div className="mx-6 mb-5 p-3 bg-amber-50 rounded-xl flex items-start gap-2">
          <i className="ri-lightbulb-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            <span className="font-600">Recomendação:</span> Arquive a conta para preservar o histórico financeiro sem que ela apareça na listagem principal. Os lançamentos continuarão nos relatórios.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onCancel} disabled={loading} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={onArchive} disabled={loading} className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap disabled:opacity-50 transition-colors">
            {loading ? 'Aguarde...' : 'Arquivar conta'}
          </button>
        </div>
      </div>
    </div>
  );
}
interface ConfirmDialogProps {
  icon: string; iconBg: string; iconColor: string; title: string; description: string;
  confirmLabel: string; confirmClass: string; cancelLabel?: string; loading: boolean;
  onCancel: () => void; onConfirm: () => void;
}
function ConfirmDialog({ icon, iconBg, iconColor, title, description, confirmLabel, confirmClass, cancelLabel = 'Cancelar', loading, onCancel, onConfirm }: ConfirmDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 p-6 text-center">
        <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
          <i className={`${icon} ${iconColor} text-xl`} />
        </div>
        <h3 className="text-base font-700 text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-5">{description}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} disabled={loading} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-50">{cancelLabel}</button>
          <button onClick={onConfirm} disabled={loading} className={`flex-1 py-2.5 ${confirmClass} text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap disabled:opacity-50`}>{loading ? 'Aguarde...' : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
